import { useCallback, useMemo } from 'react';

import {
  mapRoomToCreateRequest,
  mapRoomToUpdateRequest,
} from 'app/hooks/data/core/admin/rooms/mapping/form';
import { mapBuilderRoomToRoom } from 'app/hooks/data/core/builder/mapping/room';
import { useBuilderHotelRooms } from 'app/hooks/data/core/builder/useBuilderHotelRooms';
import { useCreateBuilderRoom } from 'app/hooks/data/core/builder/useCreateBuilderRoom';
import { useUpdateBuilderRoom } from 'app/hooks/data/core/builder/useUpdateBuilderRoom';
import { useDeleteRoom } from 'app/hooks/data/rooms/useRoom';
import { useRooms, useSaveBuilderRooms } from 'app/hooks/data/useRooms';
import type { Room } from 'app/typings';
import { DAY_PACKAGE_ROOM_CATEGORY_ID } from 'app/typings/rooms';

type UseBuilderRoomsResult = {
  rooms?: Room[];
  saveRoom: (room: Room) => void;
  deleteRoom?: (roomId: number) => Promise<unknown>;
  isSaving: boolean;
};

// isNewDataModel is undefined while the hotel is loading: no rooms query is sent
export const useBuilderRooms = (
  hotelId: number,
  isNewDataModel?: boolean
): UseBuilderRoomsResult => {
  const { data: legacyRooms } = useRooms(
    isNewDataModel === false ? hotelId : undefined
  );
  const { data: builderRooms } = useBuilderHotelRooms(
    isNewDataModel ? hotelId : undefined
  );

  const { mutate: saveLegacyRoom, isPending: isSavingLegacyRoom } =
    useSaveBuilderRooms(hotelId);
  const { mutateAsync: deleteLegacyRoom } = useDeleteRoom(hotelId);

  const { mutate: createRoom, isPending: isCreatingRoom } =
    useCreateBuilderRoom(hotelId);
  const { mutate: updateRoom, isPending: isUpdatingRoom } =
    useUpdateBuilderRoom(hotelId);

  const rooms = useMemo(() => {
    if (!isNewDataModel) {
      return legacyRooms;
    }

    return builderRooms
      ?.filter((room) => room.categoryId !== DAY_PACKAGE_ROOM_CATEGORY_ID)
      .map(mapBuilderRoomToRoom);
  }, [isNewDataModel, legacyRooms, builderRooms]);

  const saveRoom = useCallback(
    (room: Room) => {
      if (!isNewDataModel) {
        saveLegacyRoom(room);

        return;
      }

      if (room.id) {
        updateRoom({ roomId: room.id, payload: mapRoomToUpdateRequest(room) });

        return;
      }

      const { hotelId: _hotelId, ...payload } = mapRoomToCreateRequest(room);

      createRoom(payload);
    },
    [isNewDataModel, saveLegacyRoom, createRoom, updateRoom]
  );

  return {
    rooms,
    saveRoom,
    deleteRoom: isNewDataModel ? undefined : deleteLegacyRoom,
    isSaving: isSavingLegacyRoom || isCreatingRoom || isUpdatingRoom,
  };
};
